import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import EditorBox from "../editor_box/EditorBox";
import EditorShortcuts from "../editor_shortcuts/EditorShortcuts";
import Alert from "./../../common/alert/Alert";
import { Col, Button } from "react-bootstrap";
import EditorHeader from "../editor_header/EditorHeader";
import interact from "interactjs";
import {
  ADD_BOX,
  addInteractiveItem,
  changeGlobalConfig,
  changeNavItemName,
  clearPreviewFrame,
} from "../../../../common/actions";
import { resolveBoxTiming } from "../../../../common/resolveBoxTiming.es6";
import { orderBoxesByPlayhead } from "./orderBoxesByPlayhead";
import { sceneTimeBus } from "../../../../common/scene_clock.es6";
import {
  INTERACTIVE_ITEM_MIME,
  buildAddInteractiveItemFromDrop,
  findInteractiveBoxIdInState,
} from "./interactiveItemDrop";
import { isSlide, isSortableBox } from "../../../../common/utils";
import {
  aspectRatio,
  createBox,
  instanceExists,
  changeFontBase,
} from "../../../../common/common_tools";
import { sceneFontStyle } from "../../../../common/course_font";
import IdealStudio from "../../../../core/editor/main";
import i18n from "i18next";
import { SnapContainer } from "./SnapGrid";
import AlignmentGuides from "../alignment_guides/AlignmentGuides";
import RubberBand from "../rubber_band/RubberBand";
import MultiSelectShortcuts from "../multi_select_shortcuts/MultiSelectShortcuts";
import { ID_PREFIX_BOX } from "../../../../common/constants";
import { formatTimeFromFrame } from "../../canvas_workflow/formatTime";
import { shouldClearPreviewOnCanvasClick } from "./canvasPreviewClick";
import { sanitizeThemeHtml } from "../../nav_bar/scene_theme_palette/sanitizeThemeHtml";
import getMediaBoxSize from "../../../utils/getMediaBoxSize";
import { initPaletteDrag } from "../../carousel/elements_library/dnd/paletteDrag";
import { getAtomicSampleData } from "../../../../plugins/AtomicBoxPlugin/atomic_box_plugin_config";
// Frame rate used by resolveBoxTiming() across the app. The preview-frame
// integer the workflow panel emits is in FPS=30 units, so the filter below
// uses the same denominator.
const PREVIEW_FPS = 30;

// === Phase C item 6 — preview-frame visibility gate ===
// Hard-hide a box when the active preview frame falls outside its resolved
// timing range. Boxes with no startFrame (undefined → always-visible) pass
// through. Sub-items inside an InteractivePlugin are not the concern here;
// they're rendered inside their parent box and only the parent is gated.
function isBoxVisibleAtPreviewFrame(box, animations, previewFrame) {
  if (previewFrame == null) return true;
  if (!box) return true;
  const { startFrame, endFrame } = resolveBoxTiming(box, animations || []);
  if (typeof startFrame === "number" && previewFrame < startFrame) return false;
  if (typeof endFrame === "number" && previewFrame > endFrame) return false;
  return true;
}

// Shared with the workflow panel's TimelineElementNode label so the chip
// never disagrees with the node a click came from. See ./canvas_workflow/formatTime.
function formatPreviewTime(frame) {
  return formatTimeFromFrame(frame == null ? 0 : frame, PREVIEW_FPS) || "0:00";
}

let _dropSpinnerStyleInjected = false;
function showDropSpinner(x, y) {
  if (typeof document === "undefined") return () => {};
  if (!_dropSpinnerStyleInjected) {
    const styleEl = document.createElement("style");
    styleEl.textContent = "@keyframes canvasDropSpin { to { transform: rotate(360deg); } }";
    document.head.appendChild(styleEl);
    _dropSpinnerStyleInjected = true;
  }
  const node = document.createElement("div");
  Object.assign(node.style, {
    position: "fixed",
    left: `${x - 16}px`,
    top: `${y - 16}px`,
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "3px solid rgba(127,86,217,0.25)",
    borderTopColor: "#7f56d9",
    background: "rgba(255,255,255,0.6)",
    boxShadow: "0 4px 14px rgba(15,23,42,0.18)",
    pointerEvents: "none",
    zIndex: "100002",
    animation: "canvasDropSpin 0.7s linear infinite",
  });
  document.body.appendChild(node);
  let removed = false;
  const remove = () => {
    if (removed) return;
    removed = true;
    if (node.parentElement) node.parentElement.removeChild(node);
  };
  setTimeout(remove, 8000);
  return remove;
}

/**
 * EditorCanvasSli component
 * Canvas component to display slides
 */
export class EditorCanvasSli extends Component {
  constructor(props) {
    super(props);
    this.state = {
      alert: null,
      width: 0,
      height: 0,
      marginTop: 0,
      marginBottom: 0,
      fontBase: 14,
    };
    this.slideDropZoneRef = createRef();
    this.aspectRatio = this.aspectRatio.bind(this);
    this.aspectRatioListener = this.aspectRatioListener.bind(this);
    // === Phase C item 6 — bound Esc handler ===
    // IdeaTimeline only listens for Delete/Backspace (Round 19 guard), so an
    // Esc listener here cannot collide with timeline shortcuts. We early-out
    // when no preview is active so the listener is a no-op in normal authoring.
    this.handlePreviewEsc = this.handlePreviewEsc.bind(this);
  }

  handlePreviewEsc(e) {
    if (e.key !== "Escape") return;
    if (this.props.previewFrame == null) return;
    // Don't steal Esc from text editing surfaces (CKEditor, MUI inputs).
    const tag = document.activeElement?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return;
    this.props.dispatchClearPreviewFrame?.();
  }

  // `frame`: the timeline playhead (scene frame), followed through sceneTimeBus.
  _playheadOrder(boxes, frame) {
    const nowFrame = this.props.previewFrame != null ? this.props.previewFrame : frame;
    const startFrameOf = (id) => {
      const box = this.props.boxes?.[id];
      const anims = (this.props.animationsById || {})[id] || [];
      const { startFrame } = resolveBoxTiming(box, anims);
      return typeof startFrame === "number" ? startFrame : undefined; // no gate = always visible, never demoted by recency
    };
    // A box only competes on z-order recency against another box that shares
    // its `timeline.splitGroup` (today: the two halves of a split video clip
    // — see onSplitLayer in EditorApp.jsx). `timeline.startFrame` alone is NOT
    // enough to prove two boxes are a deliberate reveal pair — it's also set
    // by "insert at playhead" bookkeeping on every mid-timeline addition, by
    // screen-training import, and by manual layer-bar drags, none of which
    // imply "this should duel an unrelated box for stacking order". See
    // orderBoxesByPlayhead's header comment for the incident this guards
    // against (an explicitly "brought to front" element getting buried the
    // moment any unrelated later-timed box appears).
    const splitGroupOf = (id) => this.props.boxes?.[id]?.timeline?.splitGroup ?? undefined;
    // …but ONLY while a frame is actually being shown (workflow preview, or the
    // timeline playing/scrubbed off zero). At rest the authored `.boxes` order
    // must be exactly what the canvas paints: it is what the Layers panel, the
    // timeline row drag and the front/back buttons write, and overriding it here
    // made every layer change look like it did nothing ("katmanı değiştirdim,
    // ekranda değişmedi") and hid boxes that were authored in front.
    const framePinned = this.props.previewFrame != null || frame > 0;
    return framePinned
      ? orderBoxesByPlayhead(boxes, startFrameOf, nowFrame, splitGroupOf)
      : boxes || [];
  }

  // This scene's current playhead frame (0 while another scene's or none is published).
  _timelineFrame() {
    const item = this.props.fromCV ? this.props.containedViewSelected : this.props.navItemSelected;
    return item && sceneTimeBus.frameSceneId === item.id ? sceneTimeBus.frame : 0;
  }

  // Every render orders by the current frame; a frame re-renders only if that order changed.
  _currentOrder(boxes) {
    this._renderedOrder = this._playheadOrder(boxes, this._timelineFrame());
    return this._renderedOrder;
  }

  _onTimelineFrame() {
    const item = this.props.fromCV ? this.props.containedViewSelected : this.props.navItemSelected;
    if (!item || sceneTimeBus.frameSceneId !== item.id || !this._renderedOrder) return;
    const now = this._playheadOrder(item.boxes || [], sceneTimeBus.frame);
    const shown = this._renderedOrder;
    if (now.length !== shown.length || now.some((id, k) => id !== shown[k])) this.forceUpdate();
  }

  render() {
    let itemSelected = this.props.fromCV
      ? this.props.containedViewSelected
      : this.props.navItemSelected;
    let titles = [];
    if (itemSelected && itemSelected.id !== 0) {
      let initialTitle = this.props.viewToolbars[itemSelected.id].viewName;
      titles.push(initialTitle);
      if (!this.props.fromCV) {
        let parent = itemSelected.parent;
        while (parent !== 0) {
          titles.push(this.props.viewToolbars[parent].viewName);
          parent = this.props.navItems[parent].parent;
        }
      }
      titles.reverse();
    }

    let maincontent = document.getElementById(
      this.props.fromCV ? "contained_maincontent" : "maincontent"
    );
    let actualHeight;
    if (maincontent) {
      actualHeight = parseInt(maincontent.scrollHeight, 10);
      actualHeight =
        parseInt(maincontent.clientHeight, 10) < actualHeight ? actualHeight + "px" : "100%";
    }
    let toolbar = this.props.viewToolbars[itemSelected.id];

    let overlayHeight = actualHeight ? actualHeight : "100%";
    let boxes = itemSelected ? itemSelected.boxes : [];

    // Stacking order. Base = the scene's `.boxes` array (last id = frontmost),
    // which matches paste/append, the front/back layer buttons, the timeline and
    // the Layers panel. BUT time-staggered layers (several full-bleed images that
    // each show in a different timeline window) all render at once here, so raw
    // array order shows the wrong one on top:
    //   - at the scene start a LATER-starting layer wrongly covered the
    //     scene-start image ("en arkadaki görsel en üstte" bug);
    //   - during playback, when a later image overlaps an earlier one, the
    //     earlier (scene-start) layer wrongly stayed on top instead of the
    //     newly-revealed one.
    // Both are the same conflict at different playhead positions, so ordering is
    // made PLAYHEAD-AWARE (see orderBoxesByPlayhead): at the current frame, among
    // GATED layers (real timing-driven startFrame) that have already begun, the
    // one that began most recently floats to the front; layers not yet begun
    // stay behind; ties keep the authored order. A gate-less layer (no timeline
    // entry gate — e.g. a freshly-added text/heading box, which gets no
    // `timeline` at all) never competes on recency: it keeps its authored
    // `.boxes` position, so explicitly bringing it to front keeps it in front
    // for the whole scene instead of getting buried once a later-starting layer
    // (e.g. the second half of a split video) begins. At frame 0 (idle editing)
    // this reduces to "scene-start layers in front" — the previous a2871931
    // behaviour — so normal editing is unchanged. Frame source: workflow preview
    // freezes at previewFrame; otherwise the timeline playback clock
    // (its frame through sceneTimeBus).
    const orderedBoxes = this._currentOrder(boxes);
    let backgroundIsUri = toolbar && /data\:/.test(toolbar.background);
    let isColor = toolbar && /rgb[a]?\(\d+\,\d+\,\d+(\,[\d\.]+)?\)/.test(toolbar.background);
    let gridOn = this.props.grid && (this.props.containedViewSelected !== 0) === this.props.fromCV;
    let backgroundOpacity =
      1 -
      (toolbar && toolbar.backgroundOpacity != null ? Number(toolbar.backgroundOpacity) / 100 : 1);
    return (
      <Col
        id={this.props.fromCV ? "containedCanvas" : "canvas"}
        md={12}
        xs={12}
        className="canvasSliClass"
        onMouseDown={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey) return; // keep multi-selection
          this.props.onBoxSelected(-1);
          // Click anywhere on the canvas frame (outside any box) also exits
          // the workflow-node preview, matching the Esc affordance.
          if (this.props.previewFrame != null) {
            this.props.dispatchClearPreviewFrame?.();
          }
        }}
        onClick={(e) => {
          // Belt-and-suspenders for clicks that bypass the mousedown path
          // (synthetic .click(), some pointer-event tools, real-browser
          // mousedown swallowed mid-drag): drop the preview when the click
          // lands outside any box wrapper. EditorBox.onClick stops
          // propagation, but we still gate on a "box-*" ancestor for
          // defense in depth — see shouldClearPreviewOnCanvasClick().
          if (shouldClearPreviewOnCanvasClick(e, this.props.previewFrame)) {
            this.props.dispatchClearPreviewFrame?.();
          }
        }}
        onScroll={(e) => {
          // A slide is a fixed, centered frame that must never scroll.
          const el = e.currentTarget;
          if (el.scrollTop !== 0 || el.scrollLeft !== 0) {
            el.scrollTop = 0;
            el.scrollLeft = 0;
          }
        }}
        style={{
          display: this.props.containedViewSelected !== 0 && !this.props.fromCV ? "none" : "block",
          fontSize: this.state.fontBase ? this.state.fontBase + "px" : "14px",
          position: "relative",
          overflow: "hidden",
          width: "100%",
          height: "100%",
          padding: 0,
        }}
      >
        <div
          id={this.props.fromCV ? "airlayer_cv" : "airlayer"}
          className={"slide_air parentRestrict"}
          style={{
            margin: "auto",
            visibility: this.props.showCanvas ? "visible" : "hidden",
            width: this.state.width,
            height: this.state.height,
            marginTop: this.state.marginTop,
            marginBottom: this.state.marginBottom,
            position: "relative",
            transform: this.props.slideScale ? `scale(${this.props.slideScale})` : undefined,
            transformOrigin: "center center",
            transition: "transform 120ms ease",
          }}
        >
          <div
            id={this.props.fromCV ? "contained_maincontent" : "maincontent"}
            ref={this.slideDropZoneRef}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
                this.props.onBoxSelected(-1);
                this.setState({ showTitle: false });
                // Mirror the outer Col handler: clicking the empty inner
                // canvas area also drops the workflow-node preview.
                if (this.props.previewFrame != null) {
                  this.props.dispatchClearPreviewFrame?.();
                }
              }

              e.stopPropagation();
            }}
            onDragOver={(e) => {
              const types = e.dataTransfer && e.dataTransfer.types;
              if (types && Array.prototype.indexOf.call(types, INTERACTIVE_ITEM_MIME) !== -1) {
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
              }
            }}
            onDrop={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const enriched = buildAddInteractiveItemFromDrop(
                e.dataTransfer,
                e.clientX,
                e.clientY,
                rect
              );
              if (!enriched) return; // not our payload — leave for other handlers
              e.preventDefault();
              e.stopPropagation();
              const state = this.props.store.getState();
              const boxId = findInteractiveBoxIdInState(state);
              if (!boxId) return; // no InteractivePlugin box on this scene
              this.props.store.dispatch(addInteractiveItem(boxId, enriched));
            }}
            className={"innercanvas sli"}
            style={{
              position: "relative",
              visibility: this.props.showCanvas ? "visible" : "hidden",
              background: isColor ? toolbar.background : "",
              zIndex: "0",
              overflow: "visible",
              backgroundImage:
                !isColor && toolbar && toolbar.background
                  ? "linear-gradient(rgba(255,255,255," +
                    backgroundOpacity +
                    "), rgba(255,255,255," +
                    backgroundOpacity +
                    ")), url(" +
                    toolbar.background +
                    ")"
                  : "",
              backgroundSize:
                toolbar && toolbar.background && toolbar.backgroundAttr === "fit"
                  ? "100% 100%"
                  : toolbar &&
                      (toolbar.backgroundAttr === "centered" || toolbar.backgroundAttr === "repeat")
                    ? toolbar && toolbar.backgroundZoom !== undefined
                      ? toolbar.backgroundZoom + "%"
                      : "100%"
                    : "cover",
              backgroundRepeat:
                toolbar &&
                toolbar.background &&
                (toolbar.backgroundAttr === "centered" ||
                  toolbar.backgroundAttr === "full" ||
                  toolbar.backgroundAttr === "fit")
                  ? "no-repeat"
                  : "repeat",
              backgroundPosition:
                toolbar &&
                toolbar.background &&
                (toolbar.backgroundAttr === "centered" ||
                  toolbar.backgroundAttr === "full" ||
                  toolbar.backgroundAttr === "fit")
                  ? "center center"
                  : "0% 0%",
              ...(toolbar && toolbar.themeCssVariables
                ? toolbar.themeCssVariables
                : this.props.globalConfig?.globalTheme?.applyToAll
                  ? this.props.globalConfig.globalTheme.themeCssVariables || {}
                  : {}),
              // VERIFY-LIVE: scene default font — boxes with inline CKEditor
              // font-family still override. Set by the agent's setSceneFontAll
              // mutation (batchSetSceneFont writes toolbar.fontFamily; globalConfig
              // is the course default for new scenes).
              ...sceneFontStyle(
                (toolbar && toolbar.fontFamily) || this.props.globalConfig?.fontFamily
              ),
            }}
          >
            <RubberBand
              containerId={this.props.fromCV ? "contained_maincontent" : "maincontent"}
              outerId={this.props.fromCV ? "containedCanvas" : "canvas"}
              boxIds={boxes}
              onSelect={(ids) => this.props.onBoxSelectedAll(ids)}
            />
            {(() => {
              const activeThemeHtml =
                toolbar && toolbar.themeHtml
                  ? toolbar.themeHtml
                  : this.props.globalConfig?.globalTheme?.applyToAll &&
                      this.props.globalConfig.globalTheme.themeHtml
                    ? this.props.globalConfig.globalTheme.themeHtml
                    : null;
              return activeThemeHtml ? (
                <div
                  className="theme-html-background pointer-events-none"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 0,
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeThemeHtml(activeThemeHtml) }}
                />
              ) : null;
            })()}
            {this.state.alert}
            {this.props.syncingScene && (
              <div className="sync-scene-overlay" style={{ zIndex: 999 }}>
                <div className="sync-scene-content">
                  <div
                    className={`ai-icon-container ${this.props.syncSceneStatus === "success" ? "success" : ""}`}
                  >
                    {this.props.syncSceneStatus === "success" ? (
                      <i className="material-icons ai-sparkle success-check">check_circle</i>
                    ) : (
                      <>
                        <i className="material-icons ai-sparkle">auto_awesome</i>
                        <i className="material-icons sync-scene-spinner">sync</i>
                      </>
                    )}
                  </div>
                  <span className="sync-scene-label">
                    {this.props.syncSceneStatus === "success"
                      ? i18n.t("messages.syncScene.successMessage")
                      : this.props.syncSceneStatus === "extracting"
                        ? i18n.t("messages.syncScene.modal.busy.extract")
                        : this.props.syncSceneStatus === "motionizing"
                          ? i18n.t("messages.syncScene.modal.busy.motion")
                          : i18n.t("messages.syncScene.syncing")}
                  </span>
                </div>
              </div>
            )}
            {gridOn ? <SnapContainer key={this.props.fromCV} /> : null}
            <AlignmentGuides />
            {/* Canvas-level scene header removed: scenes never render an
                in-canvas title block. If a title is needed, the user adds an
                editable text box like any other element. */}

            <div
              style={{
                width: "100%",
                background: "black",
                height: overlayHeight,
                position: "absolute",
                top: 0,
                opacity: 0.4,
                display: "none", // (this.props.boxLevelSelected > 0) ? "block" : "none",
                visibility: "collapse", // (this.props.boxLevelSelected > 0) ? "visible" : "collapse",
              }}
            />

            {orderedBoxes.map((id) => {
              let box = boxes[id];
              // === Phase C item 6 — hard-hide gate ===
              // When a workflow node is being previewed, skip rendering any
              // top-level scene box whose resolved timing range does not
              // include previewFrame. Renderer-side short-circuit (return
              // null) keeps the DOM, dim layers, and selection state honest
              // — out-of-range boxes are truly absent, not just hidden.
              if (this.props.previewFrame != null) {
                const realBox = this.props.boxes[id];
                const anims = (this.props.animationsById || {})[id] || [];
                if (!isBoxVisibleAtPreviewFrame(realBox, anims, this.props.previewFrame)) {
                  return null;
                }
              }
              return (
                <EditorBox
                  key={id}
                  id={id}
                  grid={gridOn}
                  page={itemSelected ? itemSelected.id : 0}
                  addMarkShortcut={this.props.addMarkShortcut}
                  boxes={this.props.boxes}
                  boxSelected={this.props.boxSelected}
                  boxSelectedAll={this.props.boxSelectedAll}
                  boxLevelSelected={this.props.boxLevelSelected}
                  containedViews={this.props.containedViews}
                  containedViewSelected={this.props.containedViewSelected}
                  marks={this.props.marks}
                  pluginToolbars={this.props.pluginToolbars}
                  lastActionDispatched={this.props.lastActionDispatched}
                  deleteMarkCreator={this.props.deleteMarkCreator}
                  markCreatorId={this.props.markCreatorId}
                  onBoxAdded={this.props.onBoxAdded}
                  onBoxSelected={this.props.onBoxSelected}
                  onBoxSelectedToggle={this.props.onBoxSelectedToggle}
                  onBoxLevelIncreased={this.props.onBoxLevelIncreased}
                  onBoxMoved={this.props.onBoxMoved}
                  onBoxRotate={this.props.onBoxRotate}
                  onToolbarUpdated={this.props.onToolbarUpdated}
                  exercises={
                    itemSelected
                      ? this.props.exercises?.[itemSelected.id]?.exercises?.[id]
                      : undefined
                  }
                  onBoxResized={this.props.onBoxResized}
                  onRichMarkMoved={this.props.onRichMarkMoved}
                  onSortableContainerResized={this.props.onSortableContainerResized}
                  onBoxesInsideSortableReorder={this.props.onBoxesInsideSortableReorder}
                  onBoxDropped={this.props.onBoxDropped}
                  onVerticallyAlignBox={this.props.onVerticallyAlignBox}
                  onRichMarksModalToggled={this.props.onRichMarksModalToggled}
                  onTextEditorToggled={this.props.onTextEditorToggled}
                  setCorrectAnswer={this.props.setCorrectAnswer}
                  setCorrectAnswerForFib={this.props.setCorrectAnswerForFib}
                  onAddAnswer={this.props.onAddAnswer}
                  onRemoveAnswer={this.props.onRemoveAnswer}
                  onReorderAnswers={this.props.onReorderAnswers}
                  onAddDragArea={this.props.onAddDragArea}
                  getFibAnswers={this.props.getFibAnswers}
                  onSetFibAnswer={this.props.onSetFibAnswer}
                  pageType={itemSelected.type || 0}
                  themeId={
                    toolbar?.themeId ||
                    (this.props.globalConfig?.globalTheme?.applyToAll
                      ? this.props.globalConfig.globalTheme.themeId
                      : null) ||
                    null
                  }
                  paletteId={
                    toolbar?.paletteId ||
                    (this.props.globalConfig?.globalTheme?.applyToAll
                      ? this.props.globalConfig.globalTheme.paletteId
                      : null) ||
                    null
                  }
                  themeCssVariables={
                    toolbar?.themeCssVariables ||
                    (this.props.globalConfig?.globalTheme?.applyToAll
                      ? this.props.globalConfig.globalTheme.themeCssVariables
                      : null)
                  }
                />
              );
            })}
          </div>
          {(() => {
            // Dim overlay rendered INSIDE airlayer with negative inset
            const w = parseFloat(this.state.width) || 0;
            const h = parseFloat(this.state.height) || 0;
            if (!w || !h) return null;
            const OFF = 9999;
            const clip = `polygon(
              0 0, 100% 0, 100% 100%, 0 100%, 0 0,
              ${OFF}px ${OFF}px,
              ${OFF}px ${OFF + h}px,
              ${OFF + w}px ${OFF + h}px,
              ${OFF + w}px ${OFF}px,
              ${OFF}px ${OFF}px
            )`;
            return (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: `-${OFF}px`,
                  pointerEvents: "none",
                  background: "rgba(215, 216, 218, 0.65)",
                  clipPath: clip,
                  WebkitClipPath: clip,
                  zIndex: 1,
                }}
              />
            );
          })()}
        </div>
        {this.props.previewFrame != null ? (
          <div
            role="status"
            aria-label={`Previewing canvas at ${formatPreviewTime(this.props.previewFrame)}`}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 10,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 8px 4px 10px",
              background: "rgba(33, 33, 33, 0.85)",
              color: "#fff",
              fontSize: 12,
              lineHeight: "16px",
              borderRadius: 999,
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              fontFamily: "Ubuntu, Arial, sans-serif",
              pointerEvents: "auto",
            }}
          >
            <i className="material-icons" style={{ fontSize: 14 }}>
              visibility
            </i>
            <span>Previewing at {formatPreviewTime(this.props.previewFrame)}</span>
            <button
              type="button"
              aria-label="Clear preview"
              onClick={(e) => {
                e.stopPropagation();
                this.props.dispatchClearPreviewFrame?.();
              }}
              style={{
                marginLeft: 2,
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <i className="material-icons" style={{ fontSize: 14 }}>
                close
              </i>
            </button>
          </div>
        ) : null}
        <MultiSelectShortcuts
          boxSelectedAll={this.props.boxSelectedAll}
          pluginToolbars={this.props.pluginToolbars}
          containedViewSelected={this.props.containedViewSelected}
          onAlign={this.props.onBoxesAligned}
          onFontScale={this.props.onMultiFontScale}
        />
        <EditorShortcuts
          store={this.props.store}
          openConfigModal={this.props.openConfigModal}
          box={this.props.boxes[this.props.boxSelected]}
          containedViewSelected={this.props.containedViewSelected}
          navItemSelected={this.props.navItemSelected}
          isContained={this.props.fromCV}
          onTextEditorToggled={this.props.onTextEditorToggled}
          onAddNewAnimationModalToggled={this.props.onAddNewAnimationModalToggled}
          onBoxMoved={this.props.onBoxMoved}
          onBoxRotate={this.props.onBoxRotate}
          onBoxResized={this.props.onBoxResized}
          onBoxDeleted={this.props.onBoxDeleted}
          onToolbarUpdated={this.props.onToolbarUpdated}
          onAddNewAnimation={this.props.onAddNewAnimation}
          animationById={this.props.animationById}
          onBoxStartAnimation={this.props.onBoxStartAnimation}
          onBoxMiddleAnimation={this.props.onBoxMiddleAnimation}
          onBoxEndAnimation={this.props.onBoxEndAnimation}
          fileModalResult={this.props.fileModalResult}
          openFileModal={this.props.openFileModal}
          pointerEventsCallback={
            this.props.pluginToolbars[this.props.boxSelected] &&
            this.props.pluginToolbars[this.props.boxSelected].config &&
            this.props.pluginToolbars[this.props.boxSelected].config.name &&
            IdealStudio.Plugins.get(this.props.pluginToolbars[this.props.boxSelected].config.name)
              ? IdealStudio.Plugins.get(
                  this.props.pluginToolbars[this.props.boxSelected].config.name
                ).pointerEventsCallback
              : null
          }
          onMarkCreatorToggled={this.props.onMarkCreatorToggled}
          pluginToolbar={this.props.pluginToolbars[this.props.boxSelected]}
          pluginToolbars={this.props.pluginToolbars}
          uploadFunction={this.props.uploadFunction}
          setCorrectAnswer={this.props.setCorrectAnswerForDnd}
          onBoxSelected={this.props.onBoxSelected}
          onOneClickEditModalToggled={this.props.onOneClickEditModalToggled}
          onRichMarkEditPressed={this.props.onRichMarkEditPressed}
          marks={this.props.marks}
          base={this.props.base}
          boxSelectedAll={this.props.boxSelectedAll}
          visor={this.props.visor}
          onDuplicateBox={this.props.onDuplicateBox}
          onOpenSettings={this.props.onOpenSettings}
        />
      </Col>
    );
  }

  // The box pills (#topPillRoot) are rendered outside the canvas transform, so
  // nothing shrinks them when the stage does — at a small window or zoom they
  // covered the element they belong to. Publish the stage's own scale (canvas
  // width against the 1100px reference, times the zoom) as a CSS variable the
  // pills scale by, floored so the buttons stay usable.
  applyPillScale() {
    const isVisibleCanvas = (this.props.containedViewSelected !== 0) === !!this.props.fromCV;
    if (!isVisibleCanvas) return;
    const root = document.getElementById("topPillRoot");
    if (!root) return;
    const stage = ((this.state.fontBase || 14) / 14) * (this.props.slideScale || 1);
    const scale = Math.min(1, Math.max(0.5, stage));
    root.style.setProperty("--pill-scale", String(Math.round(scale * 1000) / 1000));
  }

  componentDidMount() {
    this._offTimelineFrame = sceneTimeBus.subscribeFrame(() => this._onTimelineFrame());
    this.applyPillScale();
    initPaletteDrag();
    interact(this.slideDropZoneRef.current).dropzone({
      accept: ".floatingEditorBox, .dnd",
      overlap: "pointer",
      ondropactivate: function (event) {
        event.target.classList.add("drop-active");
      },
      ondragenter: function (event) {
        event.target.classList.add("drop-target");
      },
      ondragleave: function (event) {
        event.target.classList.remove("drop-target");
      },
      ondrop: async function (event) {
        let mc = this.props.fromCV
          ? document.getElementById("contained_maincontent")
          : document.getElementById("maincontent");
        let al = this.props.fromCV
          ? document.getElementById("airlayer_cv")
          : document.getElementById("airlayer");
        let rect = event.target.getBoundingClientRect();
        let scale = this.props.slideScale || 1;
        let x =
          ((event.dragEvent.clientX - rect.left - mc.offsetLeft) * 100) / (mc.offsetWidth * scale);
        let y =
          ((event.dragEvent.clientY - rect.top + mc.scrollTop) * 100) / (mc.offsetHeight * scale);
        // Unclamped drop point — re-clamped per item below using the real (media) size.
        let rawX = x;
        let rawY = y;
        let namePlugin = IdealStudio.Plugins.get(event.relatedTarget.getAttribute("name"));
        let config = namePlugin ? namePlugin.getConfig() : {};
        let w = parseFloat(
          config.initialWidthSlide
            ? config.initialWidthSlide
            : config.initialWidth
              ? config.initialWidth
              : "25%"
        );
        let h = parseFloat(
          config.initialHeightSlide
            ? config.initialHeightSlide
            : config.initialHeight
              ? config.initialHeight
              : "30%"
        );
        if (w + x > 100) {
          x = 100 - w;
        }
        if (h + y > 100) {
          y = 100 - h;
        }
        let position = {
          x: x + "%",
          y: y + "%",
          type: "absolute",
        };
        if (event.relatedTarget.classList.contains("palette-item")) {
          let plugin =
            event.relatedTarget.getAttribute("data-plugin") ||
            event.relatedTarget.getAttribute("name");
          let apiPlugin = IdealStudio.Plugins.get(plugin);
          if (!apiPlugin) {
            return;
          }
          if (apiPlugin.getConfig().limitToOneInstance && instanceExists(plugin)) {
            this.setState({
              alert: (
                <Alert
                  className="pageModal"
                  show
                  hasHeader
                  backdrop={false}
                  title={
                    <span>
                      <i className="material-icons alert-warning">warning</i>
                      {i18n.t("messages.alert")}
                    </span>
                  }
                  closeButton
                  onClose={() => {
                    this.setState({ alert: null });
                  }}
                >
                  <span> {i18n.t("messages.instance_limit")} </span>
                </Alert>
              ),
            });
            return;
          }
          let itemSelected = this.props.fromCV
            ? this.props.containedViewSelected
            : this.props.navItemSelected;
          let page = itemSelected.id;
          let ids = {
            parent: page,
            container: 0,
            position: position,
            id: ID_PREFIX_BOX + Date.now(),
            page: page,
          };
          let url = event.relatedTarget.getAttribute("data-url");
          let media = event.relatedTarget.getAttribute("data-media");
          let compType = event.relatedTarget.getAttribute("data-type");
          if (compType) {
            ids.componentType = compType;
          }
          // Seed a dropped smart element with the same sample data as click-add.
          let atomicType = event.relatedTarget.getAttribute("data-atomic-type");
          if (atomicType && plugin === "AtomicBoxPlugin") {
            ids.initialState = {
              __text: encodeURIComponent(
                JSON.stringify({ elementData: getAtomicSampleData(atomicType) })
              ),
            };
            ids.width = apiPlugin.getConfig().initialWidth || "40%";
            ids.height = apiPlugin.getConfig().initialHeight || "auto";
          }
          // A card can seed the box (Şekiller: shape id + per-shape size).
          let seededState = event.relatedTarget.getAttribute("data-initial-state");
          if (seededState) {
            try {
              ids.initialState = JSON.parse(seededState);
            } catch (e) {
              /* ignore a malformed seed */
            }
          }
          let seededWidth = event.relatedTarget.getAttribute("data-width");
          let seededHeight = event.relatedTarget.getAttribute("data-height");
          if (seededWidth && seededHeight) {
            ids.width = seededWidth;
            ids.height = seededHeight;
            // Keep the seeded box on the canvas, like the media re-clamp below.
            let bw = parseFloat(seededWidth);
            let bh = parseFloat(seededHeight);
            if (!isNaN(bw) && !isNaN(bh)) {
              ids.position = {
                x: Math.max(0, Math.min(rawX, 100 - bw)) + "%",
                y: Math.max(0, Math.min(rawY, 100 - bh)) + "%",
                type: "absolute",
              };
            }
          }
          const removeDropSpinner = showDropSpinner(
            event.dragEvent.clientX,
            event.dragEvent.clientY
          );
          if (url) {
            ids.url = url;
            if (plugin === "HtmlContent") {
              // HtmlContent renders an iframe from its _html state when it's a URL.
              ids.initialState = { _html: url };
            } else if (media === "image" || media === "video") {
              try {
                let size = await getMediaBoxSize(url, media);
                ids.width = size.width;
                ids.height = size.height;
                ids.widthUnit = size.widthUnit;
                ids.heightUnit = size.heightUnit;
                // Re-clamp using the real size so the box lands exactly where the
                // drag preview showed it (top-left at the pointer, kept on-canvas).
                let bw = parseFloat(size.width);
                let bh = parseFloat(size.height);
                let px = Math.max(0, Math.min(rawX, 100 - (isNaN(bw) ? w : bw)));
                let py = Math.max(0, Math.min(rawY, 100 - (isNaN(bh) ? h : bh)));
                ids.position = { x: px + "%", y: py + "%", type: "absolute" };
              } catch (e) {
                /* keep plugin default size */
              }
            }
          }
          createBox(ids, plugin, true, this.props.onBoxAdded, this.props.boxes);
          removeDropSpinner();
          event.dragEvent.stopPropagation();
          return;
        }
        if (event.relatedTarget.classList.contains("rib")) {
          let name = event.relatedTarget.getAttribute("name");
          let apiPlugin = IdealStudio.Plugins.get(name);
          if (!apiPlugin) {
            return;
          }
          config = apiPlugin.getConfig();

          if (config.limitToOneInstance) {
            if (instanceExists(event.relatedTarget.getAttribute("name"))) {
              let alert = (
                <Alert
                  className="pageModal"
                  show
                  hasHeader
                  backdrop={false}
                  title={
                    <span>
                      <i className="material-icons alert-warning">warning</i>
                      {i18n.t("messages.alert")}
                    </span>
                  }
                  closeButton
                  onClose={() => {
                    this.setState({ alert: null });
                  }}
                >
                  <span> {i18n.t("messages.instance_limit")} </span>
                </Alert>
              );
              this.setState({ alert: alert });
              return;
            }
          }
          let itemSelected = this.props.fromCV
            ? this.props.containedViewSelected
            : this.props.navItemSelected;
          let page = itemSelected.id;
          let ids = {
            parent: page,
            container: 0,
            position: position,
            id: ID_PREFIX_BOX + Date.now(),
            page: page,
          };
          createBox(ids, name, true, this.props.onBoxAdded, this.props.boxes);
        } else {
          // Dragged box id from its DOM id; reliable when boxSelected is -1 (multi-select).
          let draggedDomId = event.relatedTarget ? event.relatedTarget.id || "" : "";
          let draggedId =
            draggedDomId.indexOf("box-") === 0
              ? draggedDomId.slice("box-".length)
              : this.props.boxSelected;
          let boxDragged = this.props.boxes[draggedId];
          let itemSelected = this.props.fromCV
            ? this.props.containedViewSelected
            : this.props.navItemSelected;
          if (
            boxDragged &&
            boxDragged.parent !== itemSelected.id &&
            (itemSelected.id !== boxDragged.parent || !isSlide(itemSelected.id))
          ) {
            this.props.onBoxDropped(
              draggedId,
              0,
              0,
              itemSelected.id,
              0,
              boxDragged.parent,
              boxDragged.container,
              position
            );
          }
          let clone = document.getElementById("clone");
          if (clone) {
            clone.parentElement.removeChild(clone);
          }
        }
        event.dragEvent.stopPropagation();
      }.bind(this),
      ondropdeactivate: function (event) {
        event.target.classList.remove("drop-active");
        event.target.classList.remove("drop-target");
      },
    });
    let calculated = this.aspectRatio(this.props, this.state);
    this.setState({ fontBase: changeFontBase(calculated.width) });

    window.addEventListener("resize", this.aspectRatioListener);
    // Refit on container resize (panel/timeline changes window.resize misses).
    const stageEl = document.getElementById(this.props.fromCV ? "containedCanvas" : "canvas");
    if (stageEl && typeof ResizeObserver !== "undefined") {
      this.stageResizeObserver = new ResizeObserver(this.aspectRatioListener);
      this.stageResizeObserver.observe(stageEl);
    }
    // === Phase C item 6 — Esc clears preview ===
    document.addEventListener("keydown", this.handlePreviewEsc);
  }

  componentWillUnmount() {
    if (this._offTimelineFrame) this._offTimelineFrame();
    interact(this.slideDropZoneRef.current).unset();
    window.removeEventListener("resize", this.aspectRatioListener);
    if (this.stageResizeObserver) {
      this.stageResizeObserver.disconnect();
      this.stageResizeObserver = null;
    }
    document.removeEventListener("keydown", this.handlePreviewEsc);
  }
  aspectRatioListener() {
    let calculated = this.aspectRatio();
    const fontBase = changeFontBase(calculated.width);
    if (fontBase !== this.state.fontBase) {
      this.setState({ fontBase });
    }
  }
  aspectRatio(props = this.props, state = this.state) {
    let ar = props.canvasRatio;
    let fromCV = props.fromCV;
    let itemSelected = fromCV ? props.containedViewSelected : props.navItemSelected;
    let customSize = itemSelected.customSize;
    let calculated = aspectRatio(
      ar,
      fromCV ? "airlayer_cv" : "airlayer",
      fromCV ? "containedCanvas" : "canvas",
      customSize
    );
    let { width, height, marginTop, marginBottom } = state;
    let current = { width, height, marginTop, marginBottom };
    if (JSON.stringify(calculated) !== JSON.stringify(current)) {
      this.setState({ ...calculated });
    }
    return calculated;
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.fontBase !== prevState.fontBase ||
      this.props.slideScale !== prevProps.slideScale ||
      this.props.containedViewSelected !== prevProps.containedViewSelected
    ) {
      this.applyPillScale();
    }
    if (
      this.props.canvasRatio !== prevProps.canvasRatio ||
      this.props.navItemSelected !== prevProps.navItemSelected
    ) {
      window.canvasRatio = this.props.canvasRatio;
      let calculated = this.aspectRatio(this.props, this.state);
      this.setState({ fontBase: changeFontBase(calculated.width) });
    }
  }
}
EditorCanvasSli.propTypes = {
  /**
   * Check if component rendered from contained view
   */
  fromCV: PropTypes.bool,
  /**
   * Slides aspect ratio
   */
  canvasRatio: PropTypes.number.isRequired,
  /**
   * Canvas show flag in current selected view
   */
  showCanvas: PropTypes.bool,
  /**
   *  Object containing all created boxes (by id)
   */
  boxes: PropTypes.object.isRequired,
  /**
   * Current Box selected. If there isn't, -1
   */
  boxSelected: PropTypes.any.isRequired,
  /**
   * Selected box level (only plugins inside plugins)
   */
  boxLevelSelected: PropTypes.number.isRequired,
  /**
   * Object containing all views (by id)
   */
  navItems: PropTypes.object.isRequired,
  /**
   * Current selected view (by ID)
   */
  navItemSelected: PropTypes.any.isRequired,
  /**
   * Object containing all contained views (identified by its ID)
   */
  containedViews: PropTypes.object.isRequired,
  /**
   * Selected contained view (by ID)
   */
  containedViewSelected: PropTypes.any.isRequired,
  /**
   * Course title
   */
  title: PropTypes.string.isRequired,
  /**
   * Object containing every view toolbar (by id)
   */
  viewToolbars: PropTypes.object.isRequired,
  /**
   * Object containing every plugin toolbar (by id)
   */
  pluginToolbars: PropTypes.object.isRequired,
  /**
   * Last action dispatched in Redux
   */
  lastActionDispatched: PropTypes.any.isRequired,
  /**
   * Callback for adding a mark shortcut
   */
  addMarkShortcut: PropTypes.func.isRequired,
  /**
   * Callback for deleting mark creator overlay
   */
  deleteMarkCreator: PropTypes.func.isRequired,
  /**
   * Identifier of the box that is creating a mark
   */
  markCreatorId: PropTypes.any.isRequired,
  /**
   * Object containing box marks
   */
  marks: PropTypes.object,
  /**
   * Callback for toggling creation mark overlay
   */
  onMarkCreatorToggled: PropTypes.func.isRequired,
  /**
   * Callback for adding a box
   */
  onBoxAdded: PropTypes.func.isRequired,
  /**
   * Callback for deleting a box
   */
  onBoxDeleted: PropTypes.func.isRequired,
  /**
   * Callback for selecting a box
   */
  onBoxSelected: PropTypes.func.isRequired,
  onBoxSelectedToggle: PropTypes.func.isRequired,
  onBoxSelectedAll: PropTypes.func.isRequired,
  onBoxesAligned: PropTypes.func.isRequired,
  onMultiFontScale: PropTypes.func.isRequired,
  /**
   * Callback for increasing box level selected (only plugins inside plugins)
   */
  onBoxLevelIncreased: PropTypes.func.isRequired,
  /**
   * Callback for moving a box
   */
  onBoxMoved: PropTypes.func.isRequired,

  /**
   * Callback for moving a box
   */
  onBoxRotate: PropTypes.func.isRequired,

  /**
   * Callback for resizing a box
   */
  onBoxResized: PropTypes.func.isRequired,
  /**
   * Callback for dropping a box
   */
  onBoxDropped: PropTypes.func.isRequired,
  /**
   *  Callback for vertically aligning boxes inside a container
   */
  onVerticallyAlignBox: PropTypes.func.isRequired,
  /**
   * Callback for reordering boxes inside a container
   */
  onBoxesInsideSortableReorder: PropTypes.func.isRequired,
  /**
   * Callback for resizing a sortable container
   */
  onSortableContainerResized: PropTypes.func.isRequired,
  /**
   * Callback for toggling CKEditor
   */
  onTextEditorToggled: PropTypes.func.isRequired,
  /**
   * Whether or not the grid is activated for slides
   */
  grid: PropTypes.bool,
  /**
   * Callback for toggling rich marks modal creator
   */
  onRichMarksModalToggled: PropTypes.func.isRequired,
  /**
   * Callback for moving marks
   */
  onRichMarkMoved: PropTypes.func.isRequired,
  /**
   * Callback for modify course title
   */
  onTitleChanged: PropTypes.func.isRequired,
  /**
   * Callback for modify navitem title and subtitle
   */
  onViewTitleChanged: PropTypes.func.isRequired,
  /**
   * Object containing all exercises
   */
  exercises: PropTypes.object,
  /**
   * Function for setting the right answer of an exercise
   */
  setCorrectAnswer: PropTypes.func.isRequired,
  /**
   * Callback for opening global configuration modal
   */
  openConfigModal: PropTypes.func,
  /**
   * Function that updates the toolbar of a view
   */
  onToolbarUpdated: PropTypes.func,
  /**
   * Last files uploaded to server or searched in modal
   */
  fileModalResult: PropTypes.object,
  /**
   * Function that opens the file search modal
   */
  openFileModal: PropTypes.func.isRequired,
  /**
   * Function for uploading files
   */
  uploadFunction: PropTypes.func,
};

// === Phase C item 6 — Redux-connected wrapper ===
// EditorCanvasSli is fed almost all of its data via getEditorCanvasProps()
// in EditorApp; we wire just the canvas-preview slice + animations through
// react-redux to avoid threading two more props through every callsite.
// animationsById is read here (rather than the parent) so resolveBoxTiming()
// agrees with the timeline/visor on legacy keyframe gates.
const mapPreviewState = (state) => {
  const preview = state.canvasPreview || {};
  const present = (state.undoGroup && state.undoGroup.present) || {};
  return {
    previewFrame: typeof preview.previewFrame === "number" ? preview.previewFrame : null,
    animationsById: present.animationsById || {},
  };
};

const mapPreviewDispatch = (dispatch) => ({
  dispatchClearPreviewFrame: () => dispatch(clearPreviewFrame()),
});

export default connect(mapPreviewState, mapPreviewDispatch)(EditorCanvasSli);
